/**
 * Raw mutation service fns for the project-folders domain.
 *
 * Relocated from `features/projects/api/projectFolders.api.ts` under
 * The matching read services + queryOptions
 * helpers (getProjectFolder, listProjectFolders, projectFolderQuery,
 * projectFolderFilesQuery, projectFoldersQuery) live canonically in
 * `entities/project-folder` since. The mutations belong to
 * this feature.
 */
import {
  ProjectFoldersService,
  type CreateFolderDto,
  type FolderDownloadManifestDto,
  type FolderResponseDto,
  type UpdateFolderDto,
} from '@inference.engine/workspace-api-client/web';
import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

export const createProjectFolder = async (
  platformProjectId: string,
  data: CreateFolderDto,
): Promise<FolderResponseDto> => {
  const response = await ProjectFoldersService.createFolder({
    client: workspaceHttp,
    path: { platformProjectId },
    body: data,
    // Modal owns error UI (inline for 409, toast for everything else); suppress
    // the global interceptor toast so the user doesn't see two stacked toasts.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to create folder');
  }

  return response.data;
};

export const updateProjectFolder = async (
  platformProjectId: string,
  folderId: string,
  data: UpdateFolderDto,
): Promise<FolderResponseDto> => {
  const response = await ProjectFoldersService.updateFolder({
    client: workspaceHttp,
    path: { platformProjectId, folderId },
    body: data,
    // Surface name-conflict (409) inline on the form; suppress global toast.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to update folder');
  }

  return response.data;
};

export const deleteProjectFolder = async (
  platformProjectId: string,
  folderId: string,
): Promise<void> => {
  await ProjectFoldersService.deleteFolder({
    client: workspaceHttp,
    path: { platformProjectId, folderId },
    // Table owns error UI (surfaces the Problem Detail via parseProblemDetail);
    // suppress the global interceptor toast so the user doesn't see two stacked toasts.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });
};

export const setProjectFolderThumbnail = async (
  platformProjectId: string,
  folderId: string,
  assetId: string | null,
): Promise<FolderResponseDto> => {
  const response = await ProjectFoldersService.setFolderThumbnail({
    client: workspaceHttp,
    path: { platformProjectId, folderId },
    body: { assetId },
  });

  if (!response.data) {
    throw new Error('Failed to update folder thumbnail');
  }

  return response.data;
};

export interface FolderApprovalChainInput {
  /** The chain new uploads into the folder get; `null` so they get none. */
  approvalChainId: string | null;
  includeSubfolders: boolean;
}

export const setProjectFolderApprovalChain = async (
  platformProjectId: string,
  folderId: string,
  input: FolderApprovalChainInput,
): Promise<FolderResponseDto> => {
  const response = await ProjectFoldersService.setFolderAutoApplyWorkflow({
    client: workspaceHttp,
    path: { platformProjectId, folderId },
    body: { workflowId: input.approvalChainId, includeSubfolders: input.includeSubfolders },
    // The dialog reports the failure itself.
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to set the folder approval chain');
  }

  return response.data;
};

export const getFolderDownloadManifest = async (
  platformProjectId: string,
  folderId: string,
): Promise<FolderDownloadManifestDto> => {
  const response = await ProjectFoldersService.getFolderDownloadManifest({
    client: workspaceHttp,
    path: { platformProjectId, folderId },
  });

  if (!response.data) {
    throw new Error('Failed to load folder download manifest');
  }

  return response.data;
};
